import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../config';
import { v4 as uuidv4 } from 'uuid';

// Collection reference
const listsCollectionRef = collection(db, 'contactLists');

// Interface for contact lists
export interface ContactList {
  id: string;
  name: string;
  description?: string;
  contactCount: number;
  createdAt: number;
  updatedAt: number;
  isSystem?: boolean;
  members?: string[];  // Array of contact IDs for small lists
  tags?: string[];     // For categorizing lists
}

// Interface for smart lists
export interface SmartList {
  id: string;
  name: string;
  rules: {
    tag?: string;
    minTransactions?: number;
  };
  createdAt: number;
  updatedAt: number;
}

// Create a new contact list
export const createContactList = async (listData: Omit<ContactList, 'id' | 'createdAt' | 'updatedAt' | 'contactCount'>): Promise<ContactList> => {
  const listId = uuidv4();
  const timestamp = Date.now();
  
  const contactList: ContactList = {
    id: listId,
    ...listData,
    contactCount: 0,
    createdAt: timestamp,
    updatedAt: timestamp
  };
  
  await setDoc(doc(listsCollectionRef, listId), contactList);
  return contactList;
};

// Get a contact list by ID
export const getContactListById = async (listId: string): Promise<ContactList | null> => {
  const listDoc = await getDoc(doc(listsCollectionRef, listId));
  
  if (!listDoc.exists()) {
    return null;
  }
  
  return listDoc.data() as ContactList;
};

// Get all contact lists
export const getContactLists = async (
  options: {
    limit?: number;
    orderBy?: 'createdAt' | 'updatedAt' | 'name' | 'contactCount';
    orderDirection?: 'asc' | 'desc';
    onlySystem?: boolean;
  } = {}
): Promise<ContactList[]> => {
  const queryConstraints = [];

  // Filter system lists if requested
  if (options.onlySystem !== undefined) {
    queryConstraints.push(where('isSystem', '==', options.onlySystem));
  }

  // Add ordering
  if (options.orderBy) {
    queryConstraints.push(orderBy(options.orderBy, options.orderDirection || 'asc'));
  } else {
    queryConstraints.push(orderBy('name', 'asc'));
  }

  // Limit results if specified
  if (options.limit) {
    queryConstraints.push(limit(options.limit));
  }

  const q = query(listsCollectionRef, ...queryConstraints);
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => (({
    ...doc.data(),
    id: doc.id
  }) as ContactList));
};

// Update a contact list
export const updateContactList = async (
  listId: string, 
  data: Partial<ContactList>
): Promise<void> => {
  const listRef = doc(listsCollectionRef, listId);
  
  // Remove properties that shouldn't be directly updated
  const { id, createdAt, ...updateData } = data as any;
  
  await updateDoc(listRef, {
    ...updateData,
    updatedAt: Date.now()
  });
};

// Delete a contact list
export const deleteContactList = async (listId: string): Promise<void> => {
  const listRef = doc(listsCollectionRef, listId);
  await deleteDoc(listRef);
};

// Add contacts to a list (for small lists that store IDs directly)
export const addContactsToList = async (
  listId: string,
  contactIds: string[]
): Promise<void> => {
  if (contactIds.length === 0) return;
  
  const listRef = doc(listsCollectionRef, listId);
  const listDoc = await getDoc(listRef);
  
  if (!listDoc.exists()) {
    throw new Error(`List not found: ${listId}`);
  }
  
  const list = listDoc.data() as ContactList;
  const existingMembers = list.members || [];
  const newMembers = contactIds.filter(id => !existingMembers.includes(id));
  
  if (newMembers.length === 0) return;
  
  // For small lists: update the members array directly
  await updateDoc(listRef, {
    members: arrayUnion(...newMembers),
    contactCount: existingMembers.length + newMembers.length,
    updatedAt: Date.now()
  });
};

// Remove contacts from a list
export const removeContactsFromList = async (
  listId: string,
  contactIds: string[]
): Promise<void> => {
  if (contactIds.length === 0) return;
  
  const listRef = doc(listsCollectionRef, listId);
  const listDoc = await getDoc(listRef);
  
  if (!listDoc.exists()) {
    throw new Error(`List not found: ${listId}`);
  }
  
  const list = listDoc.data() as ContactList;
  const existingMembers = list.members || [];
  
  // Filter to only remove contacts that are actually in the list
  const contactsToRemove = contactIds.filter(id => existingMembers.includes(id));
  
  if (contactsToRemove.length === 0) return;
  
  // For small lists: remove from the members array directly
  await updateDoc(listRef, {
    members: arrayRemove(...contactsToRemove),
    contactCount: Math.max(0, existingMembers.length - contactsToRemove.length),
    updatedAt: Date.now()
  });
};

// Get all contacts in a list (for small lists)
export const getContactsInList = async (
  listId: string
): Promise<string[]> => {
  const listDoc = await getDoc(doc(listsCollectionRef, listId));
  
  if (!listDoc.exists()) {
    throw new Error(`List not found: ${listId}`);
  }
  
  const list = listDoc.data() as ContactList;
  return list.members || [];
};

// Check if a contact is in a list
export const isContactInList = async (
  listId: string,
  contactId: string
): Promise<boolean> => {
  const listDoc = await getDoc(doc(listsCollectionRef, listId));
  
  if (!listDoc.exists()) {
    return false;
  }
  
  const list = listDoc.data() as ContactList;
  return list.members ? list.members.includes(contactId) : false;
};

// Smart lists collection reference
const smartListsRef = collection(db, 'smartLists');

// Create a new smart list
export const createSmartList = async (
  name: string,
  rules: { tag?: string; minTransactions?: number }
): Promise<SmartList> => {
  const id = uuidv4();
  const now = Date.now();
  const smartList: SmartList = {
    id,
    name,
    rules,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(doc(smartListsRef, id), smartList);
  return smartList;
};

// Get all smart lists
export const getSmartLists = async (): Promise<SmartList[]> => {
  const q = query(smartListsRef, orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => (({
    ...doc.data(),
    id: doc.id
  }) as SmartList));
};

// Delete a smart list
export const deleteSmartList = async (id: string): Promise<void> => {
  await deleteDoc(doc(smartListsRef, id));
}; 